import {useCallback, useState} from 'react'
import {useQuery} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'

interface Repository {
  id: number
  name: string
  owner: string
  nameWithOwner: string
  visibility: string
}

interface RepositoriesResponse {
  repositories: Repository[]
}

export interface UseRepositorySuggestionsOptions {
  owner: string
  enabled: boolean
  currentRepository?: string
}

export interface UseRepositorySuggestionsResult {
  repositories: Repository[]
  isLoading: boolean
  searchQuery: string
  handleFilterChange: (value: string) => void
}

export function useRepositorySuggestions({
  owner,
  enabled,
  currentRepository,
}: UseRepositorySuggestionsOptions): UseRepositorySuggestionsResult {
  const [searchQuery, setSearchQuery] = useState('')

  const {data, isLoading} = useQuery<RepositoriesResponse>({
    queryKey: ['global-nav-repo-picker', owner, searchQuery, currentRepository],
    queryFn: async () => {
      if (!owner) {
        return {repositories: []}
      }

      const url = new URL(`/_global-navigation/repositories/${encodeURIComponent(owner)}`, window.location.origin)
      if (searchQuery) {
        url.searchParams.set('query', searchQuery)
      }

      // the current repository name is sent to the server so that it can be prioritized if it is included in the results
      // repo names are unique within an owner, so we'll use that since IDs are not included in the payload
      if (currentRepository) {
        url.searchParams.set('currentRepository', currentRepository)
      }

      const response = await reactFetchJSON(url.toString())

      if (!response.ok) {
        return {repositories: []}
      }

      return response.json()
    },
    placeholderData: previousData => previousData,
    enabled: !!owner && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const repositories = data?.repositories ?? []

  const handleFilterChange = useCallback((value: string) => {
    setSearchQuery(value)
  }, [])

  return {
    repositories,
    isLoading,
    searchQuery,
    handleFilterChange,
  }
}

export type {Repository}
